import { useState, useEffect } from "react";
import {
  Search, X, RefreshCw, Mail, Send,
  FileSpreadsheet, FileText, Eye, ArrowLeft,
  Activity, BookOpen, MessageSquare, Heart, Clock,
  ShieldCheck, Smartphone, Bell, Settings, KeyRound,
  ShieldOff, ShieldCheck as ShieldOn, AlertTriangle,
} from "lucide-react";
import { exportUsersExcel, exportUsersCSV, exportUsersPDF } from "../lib/exports.js";

import { users as usersApi, subscriptions as subsApi } from "../lib/api.js";
import { useApi, useDebounce } from "../hooks/index.js";
import { Badge, Spinner, Empty, CloseX, Pagination } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  ACTIVE: "#065f46", TRIAL: "#92400e", NONE: "#374151",
  CANCELLED: "#9f1239", EXPIRED: "#9f1239", SCHOLARSHIP: "#0db87e",
};

function fmt(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function fmtTime(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function parseOs(ua = "") {
  if (!ua) return null;
  if (/Windows/i.test(ua)) return "Windows";
  if (/iPhone|iPad/i.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";
  if (/Macintosh|Mac OS/i.test(ua)) return "macOS";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown";
}

// ─── EMAIL MODAL ──────────────────────────────────────────────────────────────

function EmailModal({ user, onClose, toast }) {
  const { t } = useLang();
  const [subject, setSubject] = useState("");
  const [msg, setMsg]         = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!subject || !msg) { toast(t("users.emailRequired")); return; }
    setSending(true);
    try {
      await usersApi.sendEmail(user.id, { subject, message: msg });
      toast(t("users.emailSent", { email: user.email }));
      onClose();
    } catch (e) { toast(t("users.emailFailed", { err: e.message })); }
    finally { setSending(false); }
  };

  return (
    <div className="modal-ov open">
      <div className="modal">
        <div className="modal-ttl">{t("users.sendEmailTitle")} <CloseX onClick={onClose} /></div>
        <div style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "var(--muted)" }}>
          To: <strong>{user.name}</strong> &lt;{user.email}&gt;
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("users.emailSubject")}</label>
          <input className="f-in" value={subject} onChange={e => setSubject(e.target.value)} placeholder={t("users.emailPlaceholder")} />
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("users.emailMessage")}</label>
          <textarea className="f-ta" rows={6} value={msg} onChange={e => setMsg(e.target.value)} placeholder={t("users.messagePlaceholder")} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.cancel")}</button>
          <button className="btn btn-teal" style={{ flex: 1 }} onClick={send} disabled={sending || !subject || !msg}>
            <Send size={12} /> {sending ? t("common.sending") : t("users.sendEmailBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── RESET PASSWORD MODAL ─────────────────────────────────────────────────────

function ResetPasswordModal({ user, onClose, toast }) {
  const [sending, setSending] = useState(false);

  const confirm = async () => {
    setSending(true);
    try {
      await usersApi.resetPassword(user.id);
      toast(`✅ Password reset email sent to ${user.email}`);
      onClose();
    } catch {
      toast("❌ Failed to send reset email — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-ttl">
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <KeyRound size={16} /> Reset password
          </span>
          <CloseX onClick={onClose} />
        </div>

        {/* Icon + message */}
        <div style={{ textAlign: "center", padding: "12px 0 20px" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--amber-lo, #fef9c3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <KeyRound size={24} color="var(--amber, #d97706)" />
          </div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Send password reset email?</div>
          <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>
            A reset link will be sent to<br />
            <strong>{user.email}</strong>
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--ghost)" }}>
            The link expires in 1 hour.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" style={{ flex: 1 }} onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button className="btn btn-teal" style={{ flex: 1 }} onClick={confirm} disabled={sending}>
            <KeyRound size={12} /> {sending ? "Sending…" : "Send reset link"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── REVOKE / RESTORE ACCESS MODAL ───────────────────────────────────────────

function RevokeAccessModal({ user, action, onClose, toast, onDone }) {
  // action: "revoke" | "restore"
  const [reason, setReason] = useState("");
  const [busy, setBusy]     = useState(false);
  const isRevoke = action === "revoke";

  const confirm = async () => {
    if (isRevoke && !reason.trim()) { toast("Please enter a reason for revoking access"); return; }
    setBusy(true);
    try {
      if (isRevoke) {
        await subsApi.cancel(user.id);
      } else {
        await subsApi.restore(user.id);
      }
      toast(isRevoke
        ? `✅ Access revoked for ${user.name}`
        : `✅ Access restored for ${user.name}`
      );
      onDone();
      onClose();
    } catch (e) {
      toast(`❌ ${e.message ?? "Something went wrong"}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-ttl">
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isRevoke
              ? <ShieldOff size={16} color="#dc2626" />
              : <ShieldOn  size={16} color="#059669" />}
            {isRevoke ? "Revoke access" : "Restore access"}
          </span>
          <CloseX onClick={onClose} />
        </div>

        {/* Icon + message */}
        <div style={{ textAlign: "center", padding: "12px 0 20px" }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: isRevoke ? "#fee2e2" : "#d1fae5",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 14px",
          }}>
            {isRevoke
              ? <ShieldOff size={24} color="#dc2626" />
              : <ShieldOn  size={24} color="#059669" />}
          </div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
            {isRevoke ? "Revoke access for this user?" : "Restore access for this user?"}
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>
            <strong>{user.name}</strong> · {user.email}
          </div>
          {isRevoke && (
            <div style={{
              marginTop: 12, padding: "10px 14px", borderRadius: 8,
              background: "#fef9c3", border: "1px solid #fcd34d",
              fontSize: 12, color: "#92400e", textAlign: "left",
              display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>The user will immediately lose access to all premium content. Their subscription record is kept — access can be restored at any time.</span>
            </div>
          )}
          {!isRevoke && (
            <div style={{
              marginTop: 12, fontSize: 12, color: "var(--muted)", lineHeight: 1.5,
            }}>
              The subscription will be marked Active again and the user will regain full access.
            </div>
          )}
        </div>

        {/* Reason (revoke only) */}
        {isRevoke && (
          <div className="f-grp">
            <label className="f-lbl">Reason for revoking *</label>
            <textarea
              className="f-ta" rows={3}
              value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Payment dispute, account violation, duplicate account…"
            />
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button className="btn btn-out" style={{ flex: 1 }} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn"
            style={{
              flex: 1,
              background: isRevoke ? "#dc2626" : "#059669",
              color: "white",
              border: "none",
              opacity: busy ? 0.7 : 1,
            }}
            onClick={confirm}
            disabled={busy || (isRevoke && !reason.trim())}
          >
            {isRevoke
              ? (busy ? "Revoking…" : "Revoke access")
              : (busy ? "Restoring…" : "Restore access")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── USER DETAIL PAGE ─────────────────────────────────────────────────────────

export function PageUserDetail({ userId, toast, onNavigate, hasPermission }) {
  const { t } = useLang();
  const { data, loading, error, reload } = useApi(() => usersApi.get(userId), [userId]);
  const [emailUser, setEmailUser]                 = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [accessAction, setAccessAction]           = useState(null); // "revoke" | "restore"

  if (loading) return (
    <div>
      <button className="btn btn-out btn-sm" onClick={() => onNavigate("users")} style={{ marginBottom: 16 }}>
        <ArrowLeft size={13} /> {t("users.allUsers")}
      </button>
      <div className="card" style={{ padding: 48, textAlign: "center" }}><Spinner /></div>
    </div>
  );

  if (error || !data?.user) return (
    <div>
      <button className="btn btn-out btn-sm" onClick={() => onNavigate("users")} style={{ marginBottom: 16 }}>
        <ArrowLeft size={13} /> {t("users.allUsers")}
      </button>
      <div style={{ background: "var(--rose-lo)", border: "1px solid var(--rose)", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 24 }}>⚠️</div>
        <div style={{ fontWeight: 700 }}>{t("users.failedToLoad")}</div>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>{error ?? t("users.userNotFound")}</div>
        <button className="btn btn-out btn-sm" onClick={reload}>{t("ui.retry")}</button>
      </div>
    </div>
  );

  const u = data.user;
  const activityLogs = data.activityLogs ?? [];
  const progressDetails = data.progressDetails ?? [];
  const askGadSubs = data.askGadSubs ?? [];
  const donations = data.donations ?? [];

  const completedModules = progressDetails.filter(p => p.completed).length;
  const inProgressModules = progressDetails.filter(p => !p.completed && p.watchedPercent > 0).length;

  return (
    <div>
      {/* Back nav */}
      <button className="btn btn-out btn-sm" onClick={() => onNavigate("users")} style={{ marginBottom: 16 }}>
        <ArrowLeft size={13} /> {t("users.allUsers")}
      </button>

      {/* Profile header card */}
      <div className="card" style={{ marginBottom: 16, overflow: "hidden" }}>
        {/* Banner */}
        <div style={{ height: 6, background: "linear-gradient(90deg, var(--teal), #086b47)" }} />
        <div style={{ padding: "20px 20px 16px" }}>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Avatar */}
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg, var(--teal), #086b47)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, color: "white", fontWeight: 900, flexShrink: 0, border: "3px solid var(--teal)" }}>
              {u.avatarUrl
                ? <img src={u.avatarUrl} alt={u.name} style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                : u.name?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 20 }}>{u.name}</div>
                <span style={{ background: STATUS_COLORS[u.subscriptionStatus] ?? "#374151", color: "white", fontSize: 10, fontWeight: 700, padding: "2px 10px", borderRadius: 20, textTransform: "uppercase" }}>
                  {u.subscriptionStatus}
                </span>
                <span style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--muted)", fontSize: 10, padding: "2px 8px", borderRadius: 20 }}>{u.role}</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{u.email}</div>
              <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 11, color: "var(--ghost)" }}>📅 {t("users.joined")} {fmt(u.createdAt)}</div>
                <div style={{ fontSize: 11, color: "var(--ghost)" }}>🕐 {t("users.lastLogin")} {fmt(u.lastLoginAt)}</div>
                {u.pushToken && <div style={{ fontSize: 11, color: "var(--teal)" }}>🔔 {t("users.pushActive")}</div>}
              </div>
            </div>
            {hasPermission?.('MANAGE_USERS') && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn btn-teal btn-sm" onClick={() => setEmailUser(u)}>
                  <Mail size={12} /> {t("users.sendEmail")}
                </button>
                <button className="btn btn-out btn-sm" onClick={() => setResetPasswordUser(u)}>
                  <KeyRound size={12} /> Reset password
                </button>
                {u.subscriptionStatus === 'ACTIVE' || u.subscriptionStatus === 'TRIAL' ? (
                  <button
                    className="btn btn-sm"
                    style={{ background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" }}
                    onClick={() => setAccessAction("revoke")}
                  >
                    <ShieldOff size={12} /> Revoke access
                  </button>
                ) : u.subscriptionStatus === 'CANCELLED' ? (
                  <button
                    className="btn btn-sm"
                    style={{ background: "#d1fae5", color: "#059669", border: "1px solid #6ee7b7" }}
                    onClick={() => setAccessAction("restore")}
                  >
                    <ShieldOn size={12} /> Restore access
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
        {[
          { icon: "✅", label: t("users.modulesCompleted"), value: completedModules, color: "var(--teal)" },
          { icon: "▶️", label: t("users.inProgress"),       value: inProgressModules, color: "var(--blue)" },
          { icon: "📔", label: t("users.journalEntries"),   value: u._count?.journalEntries ?? 0, color: "#6b46c1" },
          { icon: "🎤", label: t("users.gadQuestions"),     value: u._count?.askGadSubmissions ?? 0, color: "#d69e2e" },
          { icon: "💝", label: t("users.donations"),        value: u._count?.donations ?? 0, color: "var(--rose)" },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: "14px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontFamily: "var(--fd)", fontSize: 24, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Two-column layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>

        {/* Account info */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <ShieldCheck size={14} color="var(--teal)" /> {t("users.accountInfo")}
          </div>
          {[
            ["📅", t("users.joined"), fmt(u.createdAt)],
            ["🕐", t("users.lastLogin"), fmtTime(u.lastLoginAt)],
            ["🔑", t("users.role"), u.role],
            ["🔔", t("users.pushToken"), u.pushToken ? t("users.activeToken") : t("users.notConnected")],
            ["📱", t("tbl.platform"), u.platform ?? "—"],
          ].map(([icon, label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 12 }}>
              <span style={{ color: "var(--muted)" }}>{icon} {label}</span>
              <span style={{ fontWeight: 600, color: "var(--ink)", textAlign: "right", maxWidth: "55%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Subscription */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Smartphone size={14} color="var(--blue)" /> {t("users.subscription")}
          </div>
          {u.subscription ? (
            <>
              {[
                ["💳", t("tbl.plan"), u.subscription.plan],
                ["🌐", t("tbl.platform"), u.subscription.platform],
                ["📊", t("tbl.status"), u.subscription.status],
                ["📅", t("users.renews"), fmt(u.subscription.periodEnd)],
                ["🆔", t("users.revenuecatId"), u.subscription.revenuecatSubscriberId?.slice(0, 16) ?? "—"],
              ].map(([icon, label, value]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 12 }}>
                  <span style={{ color: "var(--muted)" }}>{icon} {label}</span>
                  <span style={{ fontWeight: 600, color: "var(--ink)", textAlign: "right", maxWidth: "55%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value ?? "—"}</span>
                </div>
              ))}
              {u.subscription.mobileMoneyProvider && (
                <div style={{ marginTop: 8, padding: "6px 10px", background: "var(--amber-lo)", borderRadius: 6, fontSize: 11 }}>
                  📱 {u.subscription.mobileMoneyProvider} · {u.subscription.mobileMoneyPhone ?? "—"}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--ghost)", fontSize: 12 }}>{t("users.noActiveSubscription")}</div>
          )}
        </div>
      </div>

      {/* Child profile */}
      {u.childProfile && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>🧒 {t("users.childProfile")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 10 }}>
            {[
              [t("users.childName"), u.childProfile.childName],
              [t("users.age"), t("users.ageMonths", { n: u.childProfile.ageMonths })],
              [t("users.dateOfBirth"), fmt(u.childProfile.dateOfBirth)],
            ].map(([l, v]) => (
              <div key={l} style={{ background: "var(--teal-dim)", borderRadius: 8, padding: "8px 12px" }}>
                <div style={{ fontSize: 9, color: "var(--teal)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 2 }}>{l}</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{v ?? "—"}</div>
              </div>
            ))}
          </div>
          {u.childProfile.challenges?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, color: "var(--ghost)", marginBottom: 6 }}>{t("users.focusAreas")}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {u.childProfile.challenges.map(c => (
                  <span key={c} style={{ background: "white", border: "1px solid var(--teal-lo)", color: "var(--teal)", fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20 }}>
                    {c.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preferences */}
      {u.preferences && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Settings size={14} color="var(--muted)" /> {t("users.appPreferences")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
            {[
              ["🔔", t("users.notifications"),     u.preferences.notificationsEnabled ? t("users.on") : t("users.off")],
              ["⏰", t("users.routineReminders"),   u.preferences.routineReminderOn ? `${t("users.on")} · ${u.preferences.routineReminderTime}` : t("users.off")],
              ["🌙", t("users.darkMode"),           u.preferences.darkMode],
              ["🔤", t("users.textSize"),           u.preferences.textSize],
              ["🌐", t("users.langPref"),           u.preferences.language],
              ["📣", t("users.marketingEmails"),    u.preferences.marketingOn ? t("users.optedIn") : t("users.optedOut")],
            ].map(([icon, label, value]) => (
              <div key={label} style={{ background: "var(--surface)", borderRadius: 8, padding: "8px 12px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 2 }}>{icon} {label}</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Module progress */}
      {progressDetails.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <BookOpen size={14} color="var(--teal)" /> {t("users.moduleProgress", { n: progressDetails.length })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 300, overflowY: "auto" }}>
            {progressDetails.map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
                <span style={{ fontSize: 16 }}>{p.completed ? "✅" : "▶️"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{p.module?.code} — {p.module?.title}</div>
                  <div style={{ width: "100%", height: 4, background: "var(--border)", borderRadius: 4, marginTop: 4 }}>
                    <div style={{ height: "100%", width: `${p.watchedPercent ?? 0}%`, background: p.completed ? "var(--teal)" : "var(--blue)", borderRadius: 4, transition: "width .3s" }} />
                  </div>
                </div>
                <div style={{ fontSize: 10, color: "var(--ghost)", flexShrink: 0, textAlign: "right" }}>
                  <div>{p.watchedPercent ?? 0}%</div>
                  <div>{p.completed ? t("users.done") : t("users.inProgressStatus")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity log */}
      {activityLogs.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Activity size={14} color="var(--blue)" /> {t("users.recentActivity")}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
            {activityLogs.map(log => (
              <div key={log.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 12px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
                <span style={{ fontSize: 14, marginTop: 1 }}>
                  {log.action === "user.login" ? "🔑" : log.action?.startsWith("admin") ? "🛡" : "📌"}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, fontFamily: "var(--fm)", color: "var(--ink)" }}>{log.action}</div>
                  {log.details && <div style={{ fontSize: 11, color: "var(--ghost)" }}>{log.details}</div>}
                  {(log.ipAddress || parseOs(log.userAgent)) && (
                    <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 2 }}>
                      {log.ipAddress && `🌐 ${log.ipAddress}`}
                      {log.ipAddress && parseOs(log.userAgent) && " · "}
                      {parseOs(log.userAgent) && `💻 ${parseOs(log.userAgent)}`}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 10, color: "var(--ghost)", flexShrink: 0, textAlign: "right", fontFamily: "var(--fm)" }}>
                  {fmtTime(log.createdAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dr. Gad submissions */}
      {askGadSubs.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <MessageSquare size={14} color="#d69e2e" /> {t("users.gadSubmissions", { n: askGadSubs.length })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {askGadSubs.map(s => (
              <div key={s.id} style={{ padding: "10px 12px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span className={`badge ${s.status === "RESPONDED" ? "bg-teal" : "bg-gray"}`} style={{ fontSize: 9 }}>{s.status}</span>
                  <span style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>{fmt(s.createdAt)}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--ink2)", lineHeight: 1.5 }}>{s.questionText}</div>
                {s.responseText && (
                  <div style={{ marginTop: 6, padding: "6px 8px", background: "var(--teal-dim)", borderRadius: 6, fontSize: 11, color: "var(--teal)", lineHeight: 1.4 }}>
                    💬 {s.responseText.slice(0, 120)}{s.responseText.length > 120 ? "…" : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Donations */}
      {donations.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Heart size={14} color="var(--rose)" /> {t("users.donationHistory", { n: donations.length })}
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr><th>{t("tbl.amount")}</th><th>{t("tbl.method")}</th><th>{t("tbl.campaign")}</th><th>{t("tbl.status")}</th><th>{t("tbl.date")}</th></tr>
              </thead>
              <tbody>
                {donations.map(d => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 700, color: "var(--teal)" }}>${d.amountUsd}</td>
                    <td style={{ fontSize: 11 }}>{d.paymentMethod}</td>
                    <td style={{ fontSize: 11, color: "var(--ghost)" }}>{d.campaign ?? "—"}</td>
                    <td><Badge status={d.status} /></td>
                    <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{fmt(d.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {emailUser && (
        <EmailModal user={emailUser} onClose={() => setEmailUser(null)} toast={toast} />
      )}
      {resetPasswordUser && (
        <ResetPasswordModal user={resetPasswordUser} onClose={() => setResetPasswordUser(null)} toast={toast} />
      )}
      {accessAction && (
        <RevokeAccessModal
          user={u}
          action={accessAction}
          onClose={() => setAccessAction(null)}
          toast={toast}
          onDone={reload}
        />
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function PageUsers({ toast, search: externalSearch, onNavigate }) {
  const { t } = useLang();
  const [search, setSearch]       = useState(externalSearch ?? "");
  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);
  const [status, setStatus]       = useState("");
  const [page, setPage]           = useState(1);
  const [emailUser, setEmailUser]   = useState(null);
  const [exporting, setExporting]   = useState(false);
  const limit = 20;

  const debSearch = useDebounce(search, 400);

  const params = {
    page, limit,
    ...(debSearch && { search: debSearch }),
    ...(status    && { status }),
  };

  const { data, loading, reload } = useApi(
    () => usersApi.list(params),
    [page, debSearch, status]
  );

  const userList   = data?.users      ?? [];
  const total      = data?.total      ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const fetchAllForExport = async () => {
    setExporting(true);
    try {
      const res = await usersApi.list({ page: 1, limit: 5000, ...(debSearch && { search: debSearch }), ...(status && { status }) });
      return res?.users ?? [];
    } finally { setExporting(false); }
  };

  const handleExcelExport = async () => {
    toast(t("users.preparing", { fmt: "Excel" }));
    exportUsersExcel(await fetchAllForExport());
    toast("✅ Excel downloaded!");
  };

  const handleCSVExport = async () => {
    toast(t("users.preparing", { fmt: "CSV" }));
    exportUsersCSV(await fetchAllForExport());
    toast("✅ CSV downloaded!");
  };

  const handlePDFExport = async () => {
    toast(t("users.preparing", { fmt: "PDF" }));
    exportUsersPDF(await fetchAllForExport(), { search: debSearch, status });
    toast("✅ PDF downloaded!");
  };

  const reset = () => { setSearch(""); setStatus(""); setPage(1); };

  return (
    <div>
      {/* Header */}
      <div className="sec-hd">
        <div>
          <div className="sec-ttl">{t("users.title")}</div>
          <div className="sec-sub">
            {t("users.found", { n: total.toLocaleString() })}
            {debSearch && ` · ${debSearch}`}
            {status && ` · ${status}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-out" onClick={handleExcelExport} disabled={exporting}>
            <FileSpreadsheet size={12} /> Excel
          </button>
          <button className="btn btn-out" onClick={handlePDFExport} disabled={exporting}>
            <FileText size={12} /> PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 320, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input
            placeholder={t("users.searchPlaceholder")}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button onClick={() => { setSearch(""); setPage(1); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", display: "flex", alignItems: "center" }}>
              <X size={12} />
            </button>
          )}
        </div>
        <select className="f-sel" style={{ width: "auto" }} value={status}
          onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">{t("users.allStatus")}</option>
          <option value="active">{t("users.active")}</option>
          <option value="free">{t("users.free")}</option>
          <option value="cancelled">{t("users.cancelled")}</option>
          <option value="expired">{t("users.expired")}</option>
        </select>
        {(search || status) && (
          <button className="btn btn-out btn-sm" onClick={reset}>
            <RefreshCw size={12} /> {t("users.clear")}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card">
        {loading ? <Spinner /> : userList.length === 0
          ? <div style={{ padding: 24 }}><Empty icon="👤" message={debSearch ? t("users.noUsersMatching", { q: debSearch }) : t("users.noUsersFound")} /></div>
          : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("tbl.user")}</th><th>{t("tbl.status")}</th><th>{t("tbl.child")}</th>
                    <th>{t("tbl.plan")}</th><th>{t("tbl.platform")}</th><th>{t("tbl.joined")}</th><th>{t("tbl.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {userList.map(u => (
                    <tr key={u.id} style={{ cursor: "pointer" }}
                      onClick={() => onNavigate?.("userDetail", u.id)}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, var(--teal), #086b47)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "white", fontWeight: 700, flexShrink: 0 }}>
                            {u.name?.[0]?.toUpperCase() ?? "?"}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{u.name}</div>
                            <div style={{ fontSize: 10, color: "var(--ghost)" }}>{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td><Badge status={u.subscriptionStatus} /></td>
                      <td style={{ fontSize: 12, color: "var(--muted)" }}>
                        {u.childProfile ? `${u.childProfile.childName}, ${u.childProfile.ageMonths}mo` : "—"}
                      </td>
                      <td style={{ fontSize: 12 }}>{u.subscription?.plan ?? "—"}</td>
                      <td>
                        {u.subscription?.platform
                          ? <span className="badge bg-blue">{u.subscription.platform}</span>
                          : <span style={{ color: "var(--ghost)", fontSize: 11 }}>—</span>}
                      </td>
                      <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button className="btn btn-out btn-xs" onClick={() => onNavigate?.("userDetail", u.id)}>
                            <Eye size={10} /> {t("users.view")}
                          </button>
                          <button className="btn btn-out btn-xs" onClick={() => setEmailUser(u)}>
                            <Mail size={10} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {/* Pagination */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--ghost)" }}>
            {t("users.pageOf", { page, totalPages, total: total.toLocaleString() })}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-out btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ {t("common.prev")}</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button key={p} className={`btn btn-sm ${p === page ? "btn-teal" : "btn-out"}`} onClick={() => setPage(p)}>
                  {p}
                </button>
              );
            })}
            <button className="btn btn-teal btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t("common.next")} ›</button>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {emailUser && (
        <EmailModal
          user={emailUser}
          onClose={() => setEmailUser(null)}
          toast={toast}
        />
      )}
    </div>
  );
}
